<template>
  <div class="group/zoom relative h-full w-full select-none overflow-hidden">
    <div class="flex h-full w-full items-center justify-center">
      <div
        ref="container"
        class="flex h-full w-full touch-auto select-none items-center justify-center"
      >
        <div
          ref="element"
          class="h-full w-full p-4 lg:p-8"
          @dblclick="doubleZoomIn"
          @wheel.prevent="zoomWheel"
        >
          <LazyImage
            :key="src"
            :url="src"
            image-class="max-h-full max-w-full object-contain"
            class="h-full w-full"
          />
        </div>
      </div>
    </div>
    <transition name="slide-fade">
      <!-- group-hover/zoom:opacity-100  transition-opacity md:opacity-0 -->
      <div
        v-if="active"
        class="shadow-around absolute bottom-2 right-2 flex gap-1 rounded-xl bg-white/80 p-1.5 shadow-neutral-400/20 backdrop-blur-md dark:border-neutral-800 dark:bg-neutral-900/90 dark:shadow-none dark:ring-1 dark:ring-neutral-700/80"
      >
        <!-- -->

        <button
          :inert="scale.value <= minScale"
          class="size-8 inert:bg-gray-200 inert:text-neutral-400 dark:inert:bg-neutral-900 dark:inert:text-neutral-700 md:size-9 hover:text-primary flex cursor-pointer items-center justify-center rounded-lg bg-gray-100 text-neutral-700 transition-colors hover:bg-gray-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
          @click="zoomOut"
        >
          <LucideIcon icon="Minus" :size="20" />
        </button>
        <Select
          :value="scale"
          :options="scaleOptions"
          position="top"
          button-class="hover:text-primary md:w-13 h-8 w-11 cursor-pointer flex-col justify-center rounded-lg bg-gray-100 text-neutral-500  hover:bg-gray-200 dark:bg-neutral-800 dark:text-neutral-300  dark:hover:bg-neutral-700 md:h-9"
          :y-offset="15"
          :chevron-size="0"
          :fullscreen="!$windowSize.isDesktop && !isFullscreen"
          class="mx-0"
          :class="{
            'pointer-events-none': isFullscreen,
          }"
          @input="setScale"
        >
          <template #title> {{ $t('titles.scale') }}</template>
          <template #current-option>
            <div
              v-hint="{
                position: 'top-center',
                yOffset: 8,
                text: $t('titles.scale'),
                showDelay: 600,
              }"
              class="flex flex h-8 w-full items-center justify-center text-xs leading-[1]"
            >
              {{ Math.round(scale.value * 100) }}
              <LucideIcon icon="Percent" class="ms-[0.05rem]" :size="10" />
            </div>
          </template>
          <template #option="{ option }">
            <span class="flex items-center">
              {{ Math.round(option.value * 100) }}
              <LucideIcon icon="Percent" class="ms-[0.05rem]" :size="10" />
            </span>
          </template>
        </Select>
        <button
          :inert="scale.value >= maxScale"
          class="size-8 inert:bg-gray-200 inert:text-neutral-400 dark:inert:bg-neutral-900 dark:inert:text-neutral-700 md:size-9 hover:text-primary flex cursor-pointer items-center justify-center rounded-lg bg-gray-100 text-neutral-700 transition-colors hover:bg-gray-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
          @click="zoomIn"
        >
          <LucideIcon icon="Plus" :size="18" />
        </button>
        <button
          :inert="scale.value == 1"
          class="size-8 inert:bg-gray-200 inert:text-neutral-400 dark:inert:bg-neutral-900 dark:inert:text-neutral-700 md:size-9 hover:text-primary flex cursor-pointer items-center justify-center rounded-lg bg-gray-100 text-neutral-700 transition-colors hover:bg-gray-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
          @click="resetScale"
        >
          <LucideIcon icon="RefreshCcw" :size="16" />
        </button>
        <button
          v-if="$windowSize.isDesktop"
          v-allow-fullscreen
          class="size-8 inert:bg-gray-200 inert:text-neutral-400 dark:inert:bg-neutral-900 dark:inert:text-neutral-700 md:size-9 hover:text-primary flex cursor-pointer items-center justify-center rounded-lg bg-gray-100 text-neutral-700 transition-colors hover:bg-gray-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
          @click="toggleFullscreen"
        >
          <LucideIcon
            :icon="isFullscreen ? 'Minimize' : 'Maximize'"
            :size="16"
          />
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import Panzoom from '@panzoom/panzoom';
import LazyImage from '@/components/ui/LazyImage';

import Select from '@/components/ui/Select';
import Hint from '@/libs/directives/Hint';
import AllowFullscreen from '@/libs/directives/AllowFullscreen';

export default {
  name: 'ImageZoom',
  directives: {
    Hint,
    AllowFullscreen,
  },
  components: {
    Select,
    LazyImage,
  },
  props: {
    src: {
      type: String,
      required: true,
    },
    active: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      panzoom: null,
      scale: {
        id: 1,
        value: 1,
      },
      minScale: 0.75,
      maxScale: 3,
      scaleStep: 0.25,
      wheelStep: 0.1,

      isFullscreen: false,
    };
  },

  computed: {
    scaleOptions() {
      const options = [
        {
          id: 0,
          value: 0.75,
        },
      ];
      let id = 1;
      for (let scale = 1; scale <= this.maxScale; scale += 0.5) {
        options.push({
          id: id++,
          value: scale,
        });
      }
      return options;
    },
  },
  watch: {
    active: {
      handler: function () {
        if (this.panzoom) {
          this.destroyPanzoom();
        }
        if (this.active) {
          this.$nextTick(() => {
            this.initPanzoom();
          });
        }
      },
      immediate: true,
    },
    src: function () {
      if (this.active) {
        this.resetScale();
      }
    },
  },

  beforeDestroy() {
    if (this.panzoom) {
      this.destroyPanzoom();
    }
  },

  methods: {
    initPanzoom() {
      this.panzoom = Panzoom(this.$refs.element, {
        maxScale: this.maxScale,
        minScale: this.minScale,
        step: this.scaleStep,
      });
      document.addEventListener(
        'fullscreenchange',
        this.handleFullscreenChange
      );
    },
    destroyPanzoom() {
      this.resetScale();
      document.removeEventListener(
        'fullscreenchange',
        this.handleFullscreenChange
      );
      this.$nextTick(() => {
        this.panzoom.destroy();
      });
    },
    resetScale() {
      this.setScale({
        id: 1,
        value: 1,
      });
    },
    setScale(scaleObj) {
      this.scale = scaleObj;
      this.panzoom.zoom(this.scale.value, { animate: true });
      if (this.scale.value <= 1) {
        this.panzoom.pan(0, 0, { animate: true });
      }
    },

    doubleZoomIn() {
      this.setScale(
        this.getScaleByValue(this.scale.value + this.scaleStep * 2)
      );
    },

    zoomIn() {
      this.setScale(this.getScaleByValue(this.scale.value + this.scaleStep));
    },

    zoomOut() {
      this.setScale(this.getScaleByValue(this.scale.value - this.scaleStep));
    },

    zoomWheel(event) {
      const delta =
        event.deltaY === 0 && event.deltaX ? event.deltaX : event.deltaY;
      const wheel = delta < 0 ? 1 : -1;

      let step = this.wheelStep;
      if (this.scale.value < 1) {
        step /= 2;
      }
      let nextScaleValue = this.scale.value + step * wheel;

      this.scale = this.getScaleByValue(nextScaleValue);

      this.panzoom.zoomToPoint(this.scale.value, event, {
        animate: true,
      });
    },

    getScaleByValue(value) {
      if (value > this.maxScale) {
        value = this.maxScale;
      } else if (value < this.minScale) {
        value = this.minScale;
      }
      let nextScale = this.scaleOptions.find(
        (option) => option.value === value
      );
      if (!nextScale) {
        nextScale = {
          id: null,
          value: value,
        };
      }
      return nextScale;
    },

    async toggleFullscreen() {
      if (!document.fullscreenElement) {
        await this.$el.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    },

    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
  },
};
</script>
